import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function DataTable({ columns, data, onRowClick, emptyMsg = "No records found", pageSize = 50, showPageSize = true }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  // Reset to page 1 when data changes
  useEffect(() => { setCurrentPage(1); }, [data?.length]);

  if (!data?.length) return <div className="text-center py-12 text-white/30">{emptyMsg}</div>;

  const totalPages = rowsPerPage > 0 ? Math.ceil(data.length / rowsPerPage) : 1;
  const start = rowsPerPage > 0 ? (currentPage - 1) * rowsPerPage : 0;
  const paginatedData = rowsPerPage > 0 ? data.slice(start, start + rowsPerPage) : data;
  const end = start + paginatedData.length;
  const showPagination = totalPages > 1;

  function getPageNumbers() {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let rangeStart = Math.max(2, currentPage - 1);
      let rangeEnd = Math.min(totalPages - 1, currentPage + 1);
      if (currentPage <= 3) {
        rangeEnd = Math.min(4, totalPages - 1);
      }
      if (currentPage >= totalPages - 2) {
        rangeStart = Math.max(2, totalPages - 3);
      }
      if (rangeStart > 2) pages.push("...");
      for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
      if (rangeEnd < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  }

  const btnBase = {
    padding: "4px 10px",
    borderRadius: "6px",
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.05)",
    color: "rgba(255,255,255,0.7)",
    cursor: "pointer",
    fontSize: "13px",
    lineHeight: "1.4",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "32px",
    transition: "background 0.15s, border-color 0.15s",
  };

  const btnActive = {
    ...btnBase,
    background: "rgba(255,255,255,0.15)",
    borderColor: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontWeight: 600,
  };

  const btnDisabled = {
    ...btnBase,
    opacity: 0.35,
    cursor: "not-allowed",
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              {columns.map((col, i) => (
                <th key={i} className={`py-3 px-3 text-left text-xs uppercase tracking-wider text-white/40 font-medium ${col.className || ""}`}>{col.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, ri) => (
              <tr key={ri} onClick={() => onRowClick?.(row)} className={`border-b border-white/5 ${onRowClick ? "cursor-pointer hover:bg-white/5" : ""} transition-colors`}>
                {columns.map((col, ci) => (
                  <td key={ci} className={`py-3 px-3 text-white/80 ${col.className || ""}`}>
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(showPagination || (showPageSize && data.length > 25)) && (
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 4px 4px",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          marginTop: "4px",
          flexWrap: "wrap",
          gap: "8px",
        }}>
          {/* Left: info text + page size */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "13px", color: "rgba(255,255,255,0.45)" }}>
            <span>Showing {start + 1}–{end} of {data.length}</span>
            {showPageSize && (
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setRowsPerPage(val);
                  setCurrentPage(1);
                }}
                style={{
                  background: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: "6px",
                  color: "rgba(255,255,255,0.7)",
                  padding: "3px 8px",
                  fontSize: "13px",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
                <option value={0}>All</option>
              </select>
            )}
          </div>

          {/* Right: page controls */}
          {showPagination && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={currentPage === 1 ? btnDisabled : btnBase}
                title="Previous page"
              >
                <ChevronLeft size={14} />
              </button>
              {getPageNumbers().map((pg, i) =>
                pg === "..." ? (
                  <span key={`e${i}`} style={{ padding: "0 4px", color: "rgba(255,255,255,0.3)", fontSize: "13px" }}>...</span>
                ) : (
                  <button
                    key={pg}
                    onClick={() => setCurrentPage(pg)}
                    style={pg === currentPage ? btnActive : btnBase}
                  >
                    {pg}
                  </button>
                )
              )}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={currentPage === totalPages ? btnDisabled : btnBase}
                title="Next page"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
